/**
 * In-flight platform action per form slot.
 * Soft-event `submit=auto` waits for this so STACK does not hit a core that
 * SAME_WINDOW doAction is already replacing (WUI-572 / 4004).
 */

const counts = new Map<string, number>()
const waiters = new Map<string, Array<() => void>>()

function slotKey(slotId: string | null | undefined): string {
  return String(slotId ?? '').trim()
}

export function beginSlotAction(slotId: string | null | undefined): void {
  const key = slotKey(slotId)
  if (!key) return
  counts.set(key, (counts.get(key) ?? 0) + 1)
}

export function endSlotAction(slotId: string | null | undefined): void {
  const key = slotKey(slotId)
  if (!key) return
  const next = (counts.get(key) ?? 1) - 1
  if (next <= 0) {
    counts.delete(key)
    const list = waiters.get(key)
    waiters.delete(key)
    list?.forEach((resolve) => resolve())
    return
  }
  counts.set(key, next)
}

export function isSlotActionInFlight(slotId: string | null | undefined): boolean {
  const key = slotKey(slotId)
  return Boolean(key) && (counts.get(key) ?? 0) > 0
}

export function isAnySlotActionInFlight(): boolean {
  return counts.size > 0
}

export function waitForSlotActionIdle(slotId: string | null | undefined): Promise<void> {
  const key = slotKey(slotId)
  if (!key || !isSlotActionInFlight(key)) return Promise.resolve()
  return new Promise((resolve) => {
    const list = waiters.get(key) ?? []
    list.push(resolve)
    waiters.set(key, list)
  })
}

/** Wait until every slot that was busy at the call, and any that start before they finish, is idle. */
export async function waitForAllSlotActionsIdle(): Promise<void> {
  while (isAnySlotActionInFlight()) {
    const pending = [...counts.keys()].map((id) => waitForSlotActionIdle(id))
    await Promise.all(pending)
  }
}

/** @internal */
export function __resetSlotActionInFlightForTests(): void {
  counts.clear()
  waiters.clear()
}
